/**
 * lib/install.ts — installing Ticketless as an app, and the single browser event the whole affordance
 * rests on.
 *
 * **Chromium tells us once, and only if something is listening.** `beforeinstallprompt` fires when the
 * browser has decided the application is installable. Calling `preventDefault()` on it suppresses the
 * mini-infobar the browser would otherwise put up on its own, and hands us the moment instead — after
 * which the event object is the *only* thing in the world that can raise the native prompt. Two traps
 * follow from that, and both have bitten somebody in one of the sibling applications:
 *
 *   1. **The listener has to be attached before React mounts.** The event can fire during the first paint,
 *      and a listener registered after it has fired never hears it — there is no "is this installable?"
 *      query to ask instead, and no way to replay it. `main.tsx` therefore calls `initInstallCapture()`
 *      at module scope, above `createRoot`. Moving it into a component's effect looks tidier and quietly
 *      makes the Install button never appear on a fast machine.
 *   2. **The deferred event is one-shot.** `prompt()` may be called on it exactly once; a second call is
 *      rejected. `promptInstall` therefore drops the reference *before* it awaits, so a double tap on a
 *      slow phone cannot fire a consumed event, and `canInstall()` flips false the instant the prompt is
 *      raised rather than when it is answered.
 *
 * **iOS Safari has no such API at all**, and never will as far as anyone has announced. Adding to the home
 * screen there is a manual gesture — Share, then *Add to Home Screen* — which a page cannot trigger,
 * detect, or even ask about. `isIOS()` exists so the interface can say the words rather than draw a button
 * that would do nothing; that is the whole of its job, and it must never be used to gate anything else.
 *
 * **Installed-ness is read two ways because iOS answers the standard question wrongly.** The standard
 * signal is the `(display-mode: standalone)` media query; iOS Safari carried the non-standard
 * `navigator.standalone` for years before it honoured the manifest's `display` member (16.4), and there
 * are plenty of devices in the field on the older behaviour. Reading only one of the two reports an
 * installed app as a browser tab on exactly the platform where the manual instructions above are the only
 * way in — so the user is told to install what they already installed.
 *
 * ⚠️ **jsdom ships no `matchMedia` whatever**, so every call here is feature-detected rather than merely
 * guarded against a null result. Without that this module could not be imported by a test at all, and the
 * screens that use it would be the ones nothing could prove. A test whose SUBJECT is installed mode has to
 * stub `matchMedia` itself; this repository's convention for that lives in
 * `web/src/components/mobile.test.tsx` and is followed by `install.test.ts` next door.
 */
import { useSyncExternalStore } from "react";

/**
 * The non-standard `BeforeInstallPromptEvent`, which is in no version of `lib.dom` this repository
 * compiles against — hence the local declaration and the one cast below, rather than an augmentation of
 * `WindowEventMap`. If TypeScript ever ships it, delete this and the cast together.
 */
type BeforeInstallPromptEvent = Event & {
  readonly prompt: () => Promise<void>;
  readonly userChoice: Promise<{ readonly outcome: "accepted" | "dismissed" }>;
};

/** What a press of Install actually resolved to. `unavailable` means the prompt was never shown. */
export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

let deferred: BeforeInstallPromptEvent | null = null;
let wired = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Wire the capture. Called once from `main.tsx`, before the first render.
 *
 * Idempotent by the `wired` flag, and that is not defensive decoration: a test file calls this in its
 * `beforeEach`, and without the guard each call adds another window listener, so one dispatched event
 * would notify every subscriber two, three, four times. The subscribers here drive `useSyncExternalStore`,
 * which would then re-render for each duplicate.
 */
export function initInstallCapture(): void {
  if (typeof window === "undefined" || wired) return;
  wired = true;

  window.addEventListener("beforeinstallprompt", (event) => {
    // Suppress the browser's own mini-infobar. Without this the user is offered the install twice — once
    // by the browser, in a bar it places wherever it likes, and once by Settings — and accepting either
    // one leaves the other on screen offering something already done.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    emit();
  });

  window.addEventListener("appinstalled", () => {
    // Fired whether the install came from our button or from the browser's own menu, so this is the one
    // path that keeps the two in step. The deferred event is spent either way.
    deferred = null;
    emit();
  });
}

/** Subscribe to changes in install availability. Returns the unsubscribe, for `useSyncExternalStore`. */
export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Whether a deferred prompt is in hand and can be fired. False on iOS, and false once it is spent. */
export function canInstall(): boolean {
  return deferred !== null;
}

/** Whether this window IS the installed application — standalone display, or an iOS home-screen launch. */
export function isInstalled(): boolean {
  if (typeof window === "undefined") return false;
  const standalone =
    typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches;
  // The iOS reading. Not in `lib.dom` either, and absent on every other platform, so it is read
  // structurally rather than declared — an interface merge would put it on `Navigator` everywhere.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone || iosStandalone;
}

/**
 * Whether this is an iOS device, where installation is a gesture the user makes and not an API we call.
 *
 * The `Macintosh` clause is the part that is easy to get wrong: **iPadOS 13 and later report themselves as
 * a Mac**, user-agent and all, so a plain `/iPad/` test misses every modern iPad. Touch support is what
 * separates the two, since no Mac has a touchscreen — and `"ontouchend" in document` is the cheap form of
 * that question which does not require a touch to have happened.
 */
export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document;
}

/**
 * Fire the deferred prompt and report what the user did.
 *
 * The reference is dropped before the first `await`, so a second press while the native dialog is open
 * finds nothing to fire rather than calling `prompt()` on a consumed event. A throw is reported as
 * `unavailable` rather than as `dismissed`: the browser declining to show the dialog is not the user
 * declining the install, and `dismissed` would be a claim about somebody's intention that nothing here
 * observed. The interface answers `unavailable` with the browser-menu guidance, which is true in every
 * case that can produce it.
 */
export async function promptInstall(): Promise<InstallOutcome> {
  const event = deferred;
  if (event === null) return "unavailable";
  deferred = null;
  emit();

  try {
    await event.prompt();
    const choice = await event.userChoice;
    return choice.outcome;
  } catch {
    return "unavailable";
  }
}

/**
 * React's reading of `canInstall()`, re-rendering when it changes.
 *
 * `useSyncExternalStore` rather than a `useState` and an effect, because the value is owned by this module
 * and changes from outside React — an effect-based mirror is a second copy of the truth that is briefly
 * wrong on every mount. The third argument is the answer a server renderer would need; there is none here,
 * and "not installable" is the only honest thing to say about a page that has not reached a browser yet.
 */
export function useInstallAvailable(): boolean {
  return useSyncExternalStore(subscribeInstall, canInstall, () => false);
}
